import React, { useEffect, useState } from 'react';
import { paginePubblicate } from '../../../api-client/pagineParlanti';

/*
 * Colonna destra «parlante» delle pagine della Struttura: mostra il testo che il super admin ha scritto
 * per quell'indirizzo (menu Pagine parlanti). Se per la pagina non c'è nulla di pubblicato la colonna
 * non c'è e il contenuto resta largo come prima.
 */

/** Quale pagina parlante vale per un percorso: prima l'indirizzo identico, poi quello con «:id». */
export const trovaPaginaParlante = (pagine, pathname) => {
    const segs = String(pathname || '').replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
    const candidate = (pagine || []).filter((p) => {
        const pSegs = String(p.url || '').split('/').filter(Boolean);
        return pSegs.length === segs.length && pSegs.every((s, i) => s.startsWith(':') || s === segs[i]);
    });
    // Più segmenti fissi = più precisa
    const fissi = (p) => p.url.split('/').filter((s) => !s.startsWith(':')).length;
    return candidate.sort((a, b) => fissi(b) - fissi(a))[0] || null;
};

/**
 * Pulisce l'HTML prima di mostrarlo: niente script, stili, iframe, form, attributi on… e link javascript:.
 * Il testo lo scrive solo il super admin, ma la colonna la vedono tutte le strutture.
 */
export const pulisciHtml = (html) => {
    if (typeof DOMParser === 'undefined') return '';
    const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
    doc.querySelectorAll('script,style,iframe,object,embed,form,input,button,textarea,select,link,meta').forEach((n) => n.remove());
    doc.querySelectorAll('*').forEach((n) => {
        [...n.attributes].forEach((a) => {
            const nome = a.name.toLowerCase();
            const valore = String(a.value || '').trim().toLowerCase();
            if (nome.startsWith('on') || nome === 'style' || ((nome === 'href' || nome === 'src') && valore.startsWith('javascript:'))) {
                n.removeAttribute(a.name);
            }
        });
        if (n.tagName === 'A') { n.setAttribute('target', '_blank'); n.setAttribute('rel', 'noopener noreferrer'); }
    });
    return doc.body.firstChild ? doc.body.firstChild.innerHTML : '';
};

/** Le pagine pubblicate, lette una volta e riprese quando cambiano (in simulazione, dallo stesso browser). */
export const usePaginePubblicate = (attivo = true) => {
    const [pagine, setPagine] = useState([]);
    useEffect(() => {
        if (!attivo) return undefined;
        let vivo = true;
        const carica = () => paginePubblicate().then((r) => { if (vivo) setPagine(Array.isArray(r) ? r : []); });
        carica();
        const suCambio = (e) => { if (!e.key || e.key === 'sid-pagine-parlanti-simulazione') carica(); };
        window.addEventListener('sid-pagine-parlanti', carica);
        window.addEventListener('storage', suCambio);
        return () => {
            vivo = false;
            window.removeEventListener('sid-pagine-parlanti', carica);
            window.removeEventListener('storage', suCambio);
        };
    }, [attivo]);
    return pagine;
};

/**
 * Il riquadro della colonna: la stessa Card (`card` › `card-body text-left`) della colonna destra che
 * le pagine della Struttura hanno già (es. Nuova vendita), quindi stessa larghezza, caratteri e margini.
 * Lo usa anche l'anteprima nella scheda del super admin.
 */
export const RiquadroParlante = ({ title, html }) => (
    <div className="card sid-parlante">
        <div className="card-body text-left">
            {title && <p className="sid-parlante__titolo">{title}</p>}
            <div className="sid-parlante__testo" dangerouslySetInnerHTML={{ __html: pulisciHtml(html) }} />
        </div>
    </div>
);

/** Contenuto della pagina con, a destra, la colonna parlante se c'è. */
export const ConColonnaParlante = ({ pathname, attivo, children }) => {
    const pagine = usePaginePubblicate(attivo);
    const pagina = attivo ? trovaPaginaParlante(pagine, pathname) : null;
    if (!pagina || !String(pagina.html || '').replace(/<[^>]*>/g, '').trim()) return children;
    return (
        // Come le pagine con la colonna destra: contenuto in col-xl-9, colonna in col-xl-3
        <div className="row">
            <div className="col-xl-9">{children}</div>
            <aside className="col-xl-3" aria-label="Informazioni sulla pagina">
                <RiquadroParlante title={pagina.title} html={pagina.html} />
            </aside>
        </div>
    );
};

export default ConColonnaParlante;
